// frontend/src/services/waiverApi.js
//
// ESIGN-WAIVER-CONSENT-001 — customer-facing waiver API client.
//
// Backend contract source-of-truth (PR #93 merged 2026-05-07):
//   docs/architecture/ESIGN-WAIVER-CONSENT-001-tech.md §3.4 + §3.6.4
//   docs/backend/ESIGN-WAIVER-CONSENT-001-PR-B-contract-snapshot.md
//
// Consumed by:
//   - components/WaiverGate.js (POST /initiate then redirect to signing_url)
//   - components/WaiverReturn.js (GET /me poll for state reconciliation)
//   - components/BookSession.js (GET /me first-booking gate check)
//
// Endpoints (per PR B controller):
//
//   POST /api/waivers/initiate
//     Auth: Bearer JWT (any authenticated user). Self-serve by default;
//     admin/staff may pass { userId } to initiate for another user.
//     Body: { userId? } — optional; defaults to current user.
//     Response 200: { signingUrl: string, envelopeId: string }
//     Response 400: invalid_user_id | user_missing_email
//     Response 401: unauthenticated
//     Response 403: forbidden_cross_user_initiate
//     Response 404: user_not_found
//     Response 500: waiver_config_error | internal_error
//     Response 502: signwell_client_error | signwell_server_error |
//                   signwell_response_incomplete
//     Response 503: signwell_network_error
//
//   GET /api/waivers/me
//     Auth: Bearer JWT (any authenticated user — own status only).
//     Response 200: {
//       consentSigned: boolean,
//       consentSignedAt: string | null,    // ISO 8601 or null
//       releaseSigned: boolean,
//       releaseSignedAt: string | null,    // ISO 8601 or null
//       consentRevokedAt: string | null,   // ISO 8601 or null
//       releaseRevokedAt: string | null,   // ISO 8601 or null
//     }
//     NOTE: response shape is intentionally minimal — no PII leaks. Privacy
//     regression guard at backend asserts no extra fields past these six.
//
// Production-safety posture:
//   - Pure additive client surface — no existing endpoint contracts touched.
//   - JWT auth flows through the shared apiClient interceptor (auto-refresh).

import axios from "axios";
import { API_CONFIG, AUTH_CONFIG } from "../config";

// Reuse a slim instance so the request interceptor stamps the Authorization
// header consistently with the rest of the app. We do NOT re-import the main
// apiClient because services/api.js exports named API objects only — we follow
// the same factory pattern here for module isolation + testability.
const waiverClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

waiverClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Initiate a SignWell signing session. Returns the signingUrl the FE
 * redirects to (per Zuri spec §16.3 — full navigation, not new tab) and the
 * envelopeId the FE persists to localStorage for return-correlation.
 *
 * @param {object} [options]
 * @param {string} [options.userId] — optional admin/staff target. Members
 *   omit; controller defaults to caller's own user.
 * @returns {Promise<{ signingUrl: string, envelopeId: string }>}
 */
export const initiateWaiverSigning = (options = {}) => {
  const body = {};
  if (options && options.userId) {
    body.userId = options.userId;
  }
  return waiverClient.post("/waivers/initiate", body);
};

/**
 * Read the caller's own waiver-signing state. Used by:
 *   1. BookSession first-booking gate check.
 *   2. WaiverReturn auto-poll for webhook-race reconciliation.
 *
 * @returns {Promise<{
 *   consentSigned: boolean,
 *   consentSignedAt: string | null,
 *   releaseSigned: boolean,
 *   releaseSignedAt: string | null,
 *   consentRevokedAt: string | null,
 *   releaseRevokedAt: string | null,
 * }>}
 */
export const getMyWaiverStatus = () => {
  return waiverClient.get("/waivers/me");
};

/**
 * Convenience predicate — returns true when both consent + release are
 * currently signed AND not currently revoked. Centralizes the "fully signed"
 * check so BookSession and WaiverReturn both use identical logic. Per Zuri
 * spec §16.4.1: success state requires BOTH timestamps non-null; per the
 * mirror-flag schema, a non-null `*RevokedAt` implies the prior signature
 * is no longer in force.
 *
 * @param {object} status — payload from getMyWaiverStatus().
 * @returns {boolean}
 */
export const hasFullySignedWaivers = (status) => {
  if (!status) return false;
  return Boolean(
    status.consentSigned &&
      status.releaseSigned &&
      !status.consentRevokedAt &&
      !status.releaseRevokedAt
  );
};

/**
 * ESIGN-UX-REDESIGN-001 (Story 1) — gate-eligibility predicate. Inverse of
 * hasFullySignedWaivers, but explicitly accepts revoked-state as a gate
 * trigger per Marcus's tech doc Story 1 edge-case table:
 *   "Revoked status (consentRevokedAt or releaseRevokedAt set) — Treat as
 *    not-signed. Gate fires."
 *
 * @param {object} status — payload from getMyWaiverStatus().
 * @returns {boolean} true if the post-login gate should fire.
 */
export const needsEsignGate = (status) => {
  if (!status) return false;
  return (
    !status.consentSigned ||
      !status.releaseSigned ||
      Boolean(status.consentRevokedAt) ||
      Boolean(status.releaseRevokedAt)
  );
};

/**
 * ESIGN-UX-REDESIGN-001 (Story 2) — derive the 4-state overall status string
 * for the customer + staff-detail indicator surfaces. Per Marcus's tech doc
 * Story 2 derivation table + Zuri's indicator spec §3 vocabulary:
 *
 *   - 'signed'    — both forms signed AND neither revoked
 *   - 'partial'   — exactly one form signed AND neither revoked
 *   - 'not-signed'— neither form signed AND neither revoked
 *   - 'revoked'   — at least one *RevokedAt is non-null (revoked outranks
 *                   historical signed state for display)
 *
 * Detail-page surfaces (customer profile, staff customer-detail VitalCard)
 * derive overallStatus FE-side from the full esignStatus block. The list
 * endpoint computes overallStatus BE-side and surfaces it as
 * `esignOverallStatus` on each list item — FE consumers SHOULD prefer the
 * BE-derived value when present and fall back to deriveOverallStatus() only
 * when reading the full block from the detail endpoint.
 *
 * Pure function, no side effects, safe to call with null/undefined (returns
 * 'not-signed' as the most conservative default — never displays "signed"
 * for missing data).
 *
 * @param {object|null|undefined} block — esignStatus block (see EsignStatus
 *   type in docs/architecture/ESIGN-UX-REDESIGN-001-tech.md Data Models).
 * @returns {'signed' | 'partial' | 'not-signed' | 'revoked'}
 */
export const deriveOverallStatus = (block) => {
  if (!block) return "not-signed";
  // Honor the server-derived `revoked` boolean when present, but compute the
  // same answer locally if the block came from a path that doesn't include
  // it (defense-in-depth — keeps this predicate self-contained).
  const revoked =
    block.revoked === true ||
    Boolean(block.consentRevokedAt) ||
    Boolean(block.releaseRevokedAt);
  if (revoked) return "revoked";
  const consent = Boolean(block.consentSigned);
  const release = Boolean(block.releaseSigned);
  if (consent && release) return "signed";
  if (consent || release) return "partial";
  return "not-signed";
};

// Constants surfaced for component consumers — keep the gate route + the
// envelope-correlation localStorage key in one place to avoid drift.
export const WAIVER_GATE_PATH = "/waivers/sign-required";
export const WAIVER_RETURN_PATH = "/waivers/return";
export const PENDING_WAIVER_ENVELOPE_KEY = "pendingWaiverEnvelopeId";

const waiverApi = {
  initiateWaiverSigning,
  getMyWaiverStatus,
  hasFullySignedWaivers,
  needsEsignGate,
  deriveOverallStatus,
  WAIVER_GATE_PATH,
  WAIVER_RETURN_PATH,
  PENDING_WAIVER_ENVELOPE_KEY,
};

export default waiverApi;
